import React, { useMemo, useState } from "react";
import ModalBasic from "../../components/ModalBasic.jsx";
import { FaCheck, FaLock } from "react-icons/fa6";

const EMPTY = new Set();

/**
 * City selector modal with two modes:
 *  - "assign" (default): multi-select toggle of cities for a region. Cities
 *    assigned to a *different* region are locked (one region per city); cities
 *    already in this region are highlighted and toggle off.
 *  - "legacy": single-select. Clicking a city calls onSelect(cityId) — used to
 *    seed an empty region from a legacy city's existing cards.
 *
 * @param {boolean} props.modalOpen
 * @param {(open: boolean) => void} props.setModalOpen
 * @param {Array<{id, name, state}>} props.cities      - all available cities (normalized)
 * @param {"assign"|"legacy"} [props.mode="assign"]
 * @param {string} [props.title]                        - overrides the default title
 * @param {Set<string>} [props.regionCityIds]           - assign mode: cities in this region
 * @param {Set<string>} [props.disabledCityIds]         - assign mode: cities in other regions
 * @param {(cityId: string) => void} [props.onToggle]   - assign mode
 * @param {(cityId: string) => void} [props.onSelect]   - legacy mode
 * @param {string} [props.regionName]
 */
const CityPicker = ({
  modalOpen,
  setModalOpen,
  cities = [],
  mode = "assign",
  title,
  regionCityIds = EMPTY,
  disabledCityIds = EMPTY,
  onToggle,
  onSelect,
  regionName,
}) => {
  const [query, setQuery] = useState("");
  const isLegacy = mode === "legacy";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.state?.toLowerCase().includes(q)
    );
  }, [cities, query]);

  const defaultTitle = isLegacy
    ? "Copy from legacy city"
    : `Assign cities${regionName ? ` to ${regionName}` : ""}`;

  return (
    <ModalBasic
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      title={title || defaultTitle}
      width="lg"
    >
      <div className="flex flex-col gap-3">
        {isLegacy && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Pick a city to copy its existing web &amp; mobile cards into this
            region as a starting template.
          </p>
        )}
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search cities…"
          className="form-input w-full"
        />

        {filtered.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">
            No cities match “{query}”.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[50vh] overflow-y-auto pr-1">
            {filtered.map((city) => {
              const selected = !isLegacy && regionCityIds.has(city.id);
              const lockedElsewhere =
                !isLegacy && !selected && disabledCityIds.has(city.id);
              return (
                <button
                  key={city.id}
                  type="button"
                  disabled={lockedElsewhere}
                  onClick={() =>
                    isLegacy ? onSelect?.(city.id) : onToggle?.(city.id)
                  }
                  title={
                    lockedElsewhere ? "Already assigned to another region" : undefined
                  }
                  className={
                    "flex items-center justify-between gap-2 px-3 py-2 rounded border text-sm text-left transition-colors " +
                    (selected
                      ? "border-theme-button-primary bg-sky-50 dark:bg-sky-500/10 text-theme-button-primary"
                      : lockedElsewhere
                      ? "border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-600 cursor-not-allowed"
                      : "border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-theme-button-primary disabled:opacity-50 disabled:cursor-not-allowed")
                  }
                >
                  <span className="min-w-0">
                    <span className="block truncate">{city.name}</span>
                    {city.state && (
                      <span className="block text-xs text-slate-400 dark:text-slate-500">
                        {city.state}
                      </span>
                    )}
                  </span>
                  {selected && <FaCheck className="w-3.5 h-3.5 shrink-0" />}
                  {lockedElsewhere && <FaLock className="w-3 h-3 shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </ModalBasic>
  );
};

export default CityPicker;
